// =============================================================================
// APP LIBRARY TYPES - Apps with connection requirements and remix flow
// =============================================================================

/**
 * Supabase/OptiDev secrets that are auto-injected by the system.
 * These should NOT be added as custom secrets by app publishers.
 */
export const EXCLUDED_SUPABASE_SECRETS = [
  // Standard Supabase secrets (auto-injected during activation)
  'SUPABASE_URL',
  'SUPABASE_ANON_KEY',
  'SUPABASE_SERVICE_ROLE_KEY',
  'SUPABASE_DB_URL',
  'OPTIDEV_SUPABASE_GATEWAY_PUBLIC_KEY',
  // Workspace connection keys (auto-injected if CONNECTION key exists)
  'OPTIDEV_CONNECTION_KEY_ID',
  'OPTIDEV_CONNECTION_SECRET',
  // OptiDev platform API URL (auto-injected)
  'OPTIDEV_API_URL',
] as const;

/**
 * App categories for filtering in the App Library
 */
export enum AppCategory {
  DASHBOARDS = 'dashboards',
  PRODUCTIVITY = 'productivity',
  KIOSK = 'kiosk',
  WEBSITE = 'website',
  INTERNAL_TOOL = 'internal-tool',
  OTHER = 'other',
}

// =============================================================================
// CONNECTION REQUIREMENTS - What connections an app needs
// =============================================================================

/**
 * A connection requirement for an app.
 * Users must provide a connection matching the connectorId during remix.
 */
export interface ConnectionRequirement {
  key: string;           // Unique key for replacement mapping (e.g., 'stripe', 'source-sheet')
  connectionId: string;  // Publisher's actual connection ID (for code replacement during remix)
  connectorId: string;   // Connector type from connectors table (e.g., 'stripe', 'google-sheets')
  label: string;         // Display label (e.g., 'Stripe Account')
  description?: string;  // Help text for users
  required: boolean;     // If true, remix will fail without this connection
}

/**
 * Maps developer connection IDs to user connection IDs during remix.
 * Used to find-replace hardcoded IDs in the app's source files.
 */
export interface ConnectionReplacement {
  [key: string]: {                    // Matches ConnectionRequirement.key
    developerConnectionId: string;    // The ID used in the app's source code
    files: string[];                  // Files where this ID appears
  };
}

// =============================================================================
// EDGE FUNCTION DEPLOYMENT - Auto-deploy functions on remix
// =============================================================================

export interface EdgeFunctionConfig {
  functionName: string;       // Function name in Supabase (e.g., 'stripe-api')
  functionPath: string;       // Path relative to project root (e.g., 'supabase/functions/stripe-api')
  autoDeployOnRemix: boolean; // If true, deploy automatically after remix
}

// =============================================================================
// SUPABASE REQUIREMENTS - OptiDev Cloud (Supabase) configuration
// =============================================================================

export type SupabaseFeature = 'database' | 'auth' | 'storage' | 'edge-functions';

export interface SupabaseSecret {
  key: string;           // Secret name (e.g., 'OPENAI_API_KEY')
  label: string;         // Display label
  description?: string;  // Help text
  required: boolean;
}

export interface SupabaseRequirement {
  required: boolean;
  features: SupabaseFeature[];
  autoActivate?: boolean;  // If true, activate Supabase automatically on remix
  secrets?: SupabaseSecret[];  // Edge Function secrets remixers need to configure
}

// =============================================================================
// ENVIRONMENT VARIABLES - Secrets and API keys
// =============================================================================

export interface EnvVariable {
  key: string;           // Environment variable name (e.g., 'OPENAI_API_KEY')
  label: string;         // Display label
  description?: string;  // Help text
  required: boolean;
  masked?: boolean;      // If true, show as password field
  defaultValue?: string; // Default value if not provided
}

// =============================================================================
// DATABASE SCHEMA SNAPSHOT - Captured during submit, replayed during remix
// =============================================================================

export interface DatabaseSchemaSnapshot {
  capturedAt: string;       // ISO timestamp
  tableCount: number;       // Number of tables
  tableNames: string[];     // For display in UI
  ddlStatements: string[];  // Ordered: enums -> tables -> FKs -> indexes -> RLS -> policies
}

// =============================================================================
// APP CONFIG - Configuration stored in apps.config JSONB
// =============================================================================

export interface AppRemixConfig {
  // Connection ID replacement during remix
  connectionReplacement?: ConnectionReplacement;

  // Edge functions to auto-deploy
  edgeFunctions?: EdgeFunctionConfig[];

  // Supabase requirements
  supabase?: SupabaseRequirement;

  // Environment variables / secrets
  envVariables?: EnvVariable[];

  // Database schema snapshot (captured during submit, applied during remix)
  databaseSchema?: DatabaseSchemaSnapshot;

  // Build commands (optional, has defaults)
  buildCommand?: string;
  startCommand?: string;
  installCommand?: string;
}

// =============================================================================
// APP METADATA - Additional app information
// =============================================================================

export interface AppMetadata {
  version?: string;
  author?: string;
  framework?: string;
  repoUrl?: string;
}

// =============================================================================
// APP ENTITY - Main app interface
// =============================================================================

export interface App {
  id: string;
  name: string;
  description?: string;
  categories: string[];  // Array of AppCategory values

  // Storage
  ociPath: string;              // TAR location in OCI storage
  thumbnailUrl?: string;        // Deployment screenshot URL (auto-generated)
  customThumbnailUrl?: string;  // User-provided custom thumbnail URL
  previewUrl?: string;          // Live preview URL

  // Status
  isActive: boolean;

  // Publishing (workspace-scoped)
  workspaceId?: string;       // NULL = global (OptiDev curated)
  publishedBy?: string;       // Firebase UID of publisher
  projectId?: string;   // Project this app was published from

  // Configuration
  connections: ConnectionRequirement[];
  config: AppRemixConfig;
  metadata: AppMetadata;
  tags: string[];

  // Timestamps
  createdAt: Date;
  updatedAt: Date;
}

// =============================================================================
// APP LIST ITEM - Lightweight app for list views
// =============================================================================

export interface AppListItem {
  id: string;
  name: string;
  description?: string;
  categories: string[];  // Array of AppCategory values
  thumbnailUrl?: string;
  customThumbnailUrl?: string;
  previewUrl?: string;
  isActive: boolean;
  tags: string[];
  connections: ConnectionRequirement[];
  metadata: Pick<AppMetadata, 'author' | 'framework'>;
  // Lightweight config for display purposes (icons on cards)
  config?: {
    envVariables?: { key: string }[];  // Just need to know if any exist
    supabase?: { required: boolean };
  };
}

// =============================================================================
// REMIX DTOs - API request/response types
// =============================================================================

export interface RemixAppDto {
  projectName: string;
  connections?: Record<string, string>;  // { [requirementKey]: connectionId }
  secrets?: Record<string, string>;      // { [envVarKey]: value }
  autoActivateSupabase?: boolean;
  supabaseRegion?: string;               // AWS region code (e.g., 'us-east-2', 'eu-west-1')
  supabaseSecrets?: Record<string, string>;  // { [secretName]: value } for Edge Functions
}

export interface RemixAppResponse {
  id: string;
  name: string;
  devContainerId: string;
  status: string;
  accessUrl: string;
}

export interface ValidateAppConnectionsDto {
  connections: Record<string, string>;  // { [requirementKey]: connectionId }
}

export interface ValidateAppConnectionsResponse {
  valid: boolean;
  errors?: Record<string, string>;  // { [requirementKey]: errorMessage }
}

// =============================================================================
// PUBLISH DTOs - Publishing project as app
// =============================================================================

export interface PublishAppDto {
  projectId: string;
  name: string;
  description?: string;
  categories: string[];  // Array of AppCategory values (min 1)
  tags?: string[];
  thumbnailUrl?: string;
  customThumbnailUrl?: string;
  previewUrl?: string;
  connections?: ConnectionRequirement[];
  config?: AppRemixConfig;
  metadata?: AppMetadata;
}

export interface UpdateAppDto {
  name?: string;
  description?: string;
  categories?: string[];  // Array of AppCategory values
  tags?: string[];
  thumbnailUrl?: string;
  customThumbnailUrl?: string;
  previewUrl?: string;
  isActive?: boolean;
  connections?: ConnectionRequirement[];
  config?: AppRemixConfig;
  metadata?: AppMetadata;
}
